import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { useARC } from "./ARC/ARCProvider";

import { ConnectionState } from "lib/consts/ARC/connection";
import { DefaultEnableControl, robotStatesInfo } from "lib/consts/robot";
import {
    attachRosTopicWithRetry,
    LATE_ROS_TOPIC_OPTIONS,
} from "lib/ros/rosTopicSubscribeWithRetry";
import { configHeight, configMode, configMotorPower, performMovement } from "lib/protocols/robot";

export const RobotControlContext = createContext();
RobotControlContext.displayName = "RobotControl";
export const useRobotControl = () => useContext(RobotControlContext);

/** If no `/state/battery` message within this window, UI treats level as N/A (`null`). */
const BATTERY_STALE_MS = 10_000;
const BATTERY_STALE_CHECK_MS = 1000;

/** Latched robot state (same QoS as bridge `/state/modes` / `/state/connected`). */
const LATCHED_STATE_SUBSCRIBE_QOS = {
    durability: "transient_local",
    reliability: "reliable",
    depth: 1,
};

const defaultRobotStates = () =>
    Object.entries(robotStatesInfo).reduce(
        (result, [key, value]) => ((result[key] = value.default), result),
        {}
    );

const RobotControlProvider = ({ children }) => {
    const { controlSocket, connectionState, sendCommand: send } = useARC();

    const batteryLastReceivedAtRef = useRef(0);

    const [states, setStates] = useState(defaultRobotStates);
    const [enableControl, setEnableControl] = useState(DefaultEnableControl);
    const [showControl, setShowControl] = useState(enableControl);

    const setState = useCallback((key, value) => {
        if (key === "battery") {
            batteryLastReceivedAtRef.current = Date.now();
        }
        setStates((prev) => ({ ...prev, [key]: value }));
    }, []);

    const sendMotorPower = useCallback((action) => send(configMotorPower(action)), [send]);
    const sendMode = useCallback((mode) => send(configMode(mode)), [send]);
    const sendHeight = useCallback((height) => send(configHeight(height)), [send]);
    const sendPerformMovement = useCallback((movement) => send(performMovement(movement)), [send]);

    useEffect(() => {
        setEnableControl(connectionState === ConnectionState.Connected);
        setShowControl(connectionState === ConnectionState.Connected);
        if (connectionState !== ConnectionState.Connected) {
            batteryLastReceivedAtRef.current = 0;
            // Drop profile-specific modes (e.g. Lynx FLAT_AGILE) so ModeBar does not
            // keep advertising them after the bridge is gone / before the next profile publishes.
            setStates((prev) => ({
                ...prev,
                battery: null,
                modes: robotStatesInfo.modes.default,
                mode: robotStatesInfo.mode.default,
            }));
        }
    }, [connectionState]);

    useEffect(() => {
        if (connectionState !== ConnectionState.Connected) {
            return;
        }
        const id = window.setInterval(() => {
            const last = batteryLastReceivedAtRef.current;
            if (last === 0) {
                return;
            }
            if (Date.now() - last > BATTERY_STALE_MS) {
                setStates((prev) => {
                    if (prev.battery == null) {
                        return prev;
                    }
                    batteryLastReceivedAtRef.current = 0;
                    return { ...prev, battery: null };
                });
            }
        }, BATTERY_STALE_CHECK_MS);
        return () => window.clearInterval(id);
    }, [connectionState]);

    useEffect(() => {
        if (!controlSocket || connectionState !== ConnectionState.Connected) {
            return;
        }

        const cleanups = Object.entries(robotStatesInfo).map(([key, value]) =>
            attachRosTopicWithRetry(
                controlSocket,
                value.topic,
                (data) => setState(key, value.transform ? value.transform(data) : data),
                {
                    ...LATE_ROS_TOPIC_OPTIONS,
                    ...(key === "modes"
                        ? { subscribeQos: LATCHED_STATE_SUBSCRIBE_QOS }
                        : {}),
                    markMessageReceived:
                        key === "battery"
                            ? (data) => typeof data === "number" && !Number.isNaN(data)
                            : undefined,
                }
            )
        );

        return () => cleanups.forEach((cleanup) => cleanup());
    }, [controlSocket, connectionState, setState]);

    return (
        <RobotControlContext.Provider
            value={{
                enableControl,
                setEnableControl,
                showControl,
                setShowControl,
                ...states,
                setRobotState: setState,
                sendMotorPower,
                sendMode,
                sendHeight,
                sendPerformMovement,
            }}
        >
            {children}
        </RobotControlContext.Provider>
    );
};

export default RobotControlProvider;
